import * as React from 'react';
import { StyledInput } from '../JRCStyledInput';
import { JRCInputFieldProps } from '../Common/JRCFormFieldRenderer.types';
import { JRCFormFieldRenderer } from '../Common/JRCFormFieldRenderer';
import { DataCy } from '../../../../types/dataAttributes';
import { FieldValues } from 'react-hook-form';
import jamespot, { ApiWrapper, jFileLittle } from 'jamespot-user-api';
import { JRCLoader } from '../../../JRCLoader/JRCLoader';
import styled from 'styled-components';
import { useState } from 'react';

export type NativeInputFileProps = DataCy &
    Omit<React.ComponentPropsWithoutRef<'input'>, 'value' | 'onChange'> & {
        // token is MANDATORY but can be given asynchronously
        token?: string | undefined;
        attrName?: string;
        onLoading?: (state: boolean) => void;
        // override default html value declaration
        value: ApiWrapper<jFileLittle>[];
        // override default html onChange declaration
        onChange: (files: ApiWrapper<jFileLittle>[]) => void;
        showLoading?: boolean;
    };

export type JRCInputFileProps<T extends FieldValues = FieldValues> = JRCInputFieldProps<T> &
    Omit<NativeInputFileProps, 'value' | 'onChange'>;

const Container = styled.div`
    display: flex;
    gap: 10px;
`;

const InputFileElement = (
    { onLoading, showLoading, ...props }: NativeInputFileProps,
    ref: React.ForwardedRef<HTMLInputElement>,
) => {
    const [loading, setLoading] = useState(false);

    if (!props.name || !props.token) return null;

    function onChange(e: React.ChangeEvent<HTMLInputElement>) {
        const token = props.token;
        if (e.target.files && token) {
            setLoading(true);
            onLoading?.(true);
            jamespot.file
                .uploadMany(
                    Array.from(e.target.files).map((file) => ({
                        attrName: props.attrName ?? props.name ?? '',
                        file,
                        token: token,
                    })),
                )
                .then((res) => {
                    const files = res.map((r) => r.response);
                    props.onChange(files);
                })
                .finally(() => {
                    setLoading(false);
                    onLoading?.(false);
                });
        }
    }

    return (
        <Container>
            <StyledInput
                type="file"
                id={props.name}
                {...props}
                // value cannot be set programmatically to anything else than undefined or ""
                value={undefined}
                onChange={onChange}
                ref={ref}
            />
            {loading && showLoading && <JRCLoader />}
        </Container>
    );
};
export const InputFile = React.forwardRef(InputFileElement);

/**
 * Component used as a <input type="file"/>
 * @param props JRCInputFieldProps
 * validation props: required
 * @returns JSX.Element
 */
export function JRCInputFile<T extends FieldValues = FieldValues>(props: JRCInputFileProps<T>) {
    return (
        <JRCFormFieldRenderer
            {...props}
            renderFunction={({ value, ...field }) => <InputFile {...props} {...field} value={value as any} />}
        />
    );
}
